import { ExternalLinkIcon, FileTextIcon, InfoIcon, ShoppingBagIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { useState } from 'react'
import useSWR from 'swr'
import {
  AspectRatio,
  Badge,
  Box,
  Button,
  Callout,
  DataList,
  Dialog,
  Flex,
  Grid,
  Heading,
  Link,
  Spinner,
  Tabs,
  Text,
} from '@radix-ui/themes'

import { makeUrlImage, makeUrlVehicle } from '@/hooks/connectionData'
import { formatWithThousandSep } from '@/utils/utils'
import Vehicle from '@/interfaces/Vehicle'
import { api } from '@/utils/api'

export const VehicleDialog = ({ id, onClose }: { id: string; onClose: () => void }) => {
  const { data, isLoading } = useSWR(makeUrlVehicle(id), (url) => api.get<Vehicle>(url))
  const [showReport, setShowReport] = useState(false)
  const { t } = useTranslation()

  const vehicle = data?.data

  return (
    <>
      <Dialog.Root
        defaultOpen
        onOpenChange={(open) => {
          !open && onClose()
        }}
      >
        <Dialog.Content size="4" maxWidth="75%" aria-describedby={undefined}>
          <Dialog.Title>Detalles del vehículo</Dialog.Title>

          {isLoading || !vehicle ? (
            <Flex gap="2">
              <Spinner />
              <Text>Cargando...</Text>
            </Flex>
          ) : (
            <>
              <Flex gap="8">
                <Flex flexGrow="2">
                  <AspectRatio ratio={1}>
                    <img
                      src={makeUrlImage(vehicle.pictures.public?.[0])}
                      style={{
                        borderRadius: 'var(--radius-2)',
                        objectFit: 'cover',
                        height: '100%',
                        width: '100%',
                      }}
                    />
                  </AspectRatio>
                </Flex>

                <Flex direction="column" flexGrow="1" gap="2">
                  <Flex direction="column">
                    <Heading align="right">
                      {vehicle.brand} {vehicle.model} {vehicle.year}
                    </Heading>
                    {vehicle.version && (
                      <Text color="gray" size="1" align="right">
                        {vehicle.version}
                      </Text>
                    )}
                  </Flex>
                  <Heading color="red" align="right">
                    ${formatWithThousandSep(vehicle.price)}
                  </Heading>
                  <Button>
                    <ShoppingBagIcon />
                    <Text>Comprar</Text>
                  </Button>
                  <Button color="gray" onClick={() => setShowReport(true)}>
                    <FileTextIcon />
                    <Text>Reporte</Text>
                  </Button>
                </Flex>
              </Flex>

              <Tabs.Root defaultValue="pictures.public">
                <Tabs.List>
                  <Tabs.Trigger value="pictures.public">
                    <Text>Imágenes Públicas</Text>
                    <Badge ml="2" color="gray" radius="full">
                      {vehicle.pictures.public?.length ?? 0}
                    </Badge>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="pictures.registry">
                    <Text>Imágenes Registro</Text>
                    <Badge ml="2" color="gray" radius="full">
                      {vehicle.pictures.registry?.length ?? 0}
                    </Badge>
                  </Tabs.Trigger>
                </Tabs.List>

                <Box pt="3">
                  <Tabs.Content value="pictures.public">
                    {vehicle.pictures.public?.length ? (
                      <Grid gap="2" columns="5">
                        {vehicle.pictures.public.map((image) => (
                          <AspectRatio
                            style={{
                              backgroundColor: 'var(--gray-6)',
                              borderRadius: 'var(--radius-3)',
                              overflow: 'hidden',
                            }}
                            key={image}
                            ratio={1}
                          >
                            <img
                              src={makeUrlImage(image)}
                              style={{
                                objectFit: 'cover',
                                height: '100%',
                                width: '100%',
                              }}
                            />
                          </AspectRatio>
                        ))}
                      </Grid>
                    ) : (
                      <Callout.Root color="gray">
                        <Callout.Icon>
                          <InfoIcon />
                        </Callout.Icon>
                        <Callout.Text>Sin imágenes públicas.</Callout.Text>
                      </Callout.Root>
                    )}
                  </Tabs.Content>

                  <Tabs.Content value="pictures.registry">
                    {vehicle.pictures.registry?.length ? (
                      <Grid gap="2" columns="5">
                        {vehicle.pictures.registry.map((image) => (
                          <AspectRatio
                            style={{
                              backgroundColor: 'var(--gray-6)',
                              borderRadius: 'var(--radius-3)',
                              overflow: 'hidden',
                            }}
                            key={image}
                            ratio={1}
                          >
                            <img
                              src={makeUrlImage(image)}
                              style={{
                                borderRadius: 'var(--radius-2)',
                                objectFit: 'cover',
                                height: '100%',
                                width: '100%',
                              }}
                            />
                          </AspectRatio>
                        ))}
                      </Grid>
                    ) : (
                      <Callout.Root color="gray">
                        <Callout.Icon>
                          <InfoIcon />
                        </Callout.Icon>
                        <Callout.Text>Sin imágenes de registro.</Callout.Text>
                      </Callout.Root>
                    )}
                  </Tabs.Content>
                </Box>
              </Tabs.Root>
            </>
          )}
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button onClick={() => onClose()}>
                <Text>Cerrar</Text>
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {vehicle && (
        <Dialog.Root open={showReport}>
          <Dialog.Content maxWidth="30em">
            <Flex gap="5" direction="column">
              <Dialog.Title>Reporte</Dialog.Title>
              <DataList.Root>
                <DataList.Item>
                  <DataList.Label>Patente</DataList.Label>
                  <DataList.Value>{vehicle.plate}</DataList.Value>
                  <DataList.Label>Kilómetros</DataList.Label>
                  <DataList.Value>{formatWithThousandSep(vehicle.odometer)}km</DataList.Value>
                  <DataList.Label>Dueños</DataList.Label>
                  <DataList.Value>{vehicle.owners}</DataList.Value>
                  <DataList.Label>Llaves</DataList.Label>
                  <DataList.Value>{vehicle.keys}</DataList.Value>
                  <DataList.Label>Color</DataList.Label>
                  <DataList.Value>{vehicle.color_format}</DataList.Value>
                  <DataList.Label>Mantención</DataList.Label>
                  <DataList.Value>{t(vehicle.maintenance.status)}</DataList.Value>
                  <DataList.Label>Revisión Técnica</DataList.Label>
                  <DataList.Value>{format(vehicle.documents.checkup, 'PPP')}</DataList.Value>
                  <DataList.Label>Seguro Obligatorio</DataList.Label>
                  <DataList.Value>{t(vehicle.documents.insurance)}</DataList.Value>
                  <DataList.Label>Permiso de Circulación</DataList.Label>
                  <DataList.Value>{t(vehicle.documents.permit)}</DataList.Value>
                  <DataList.Label>Comentarios</DataList.Label>
                  <DataList.Value>{vehicle.comments ?? '-'}</DataList.Value>
                  <DataList.Label>Informe</DataList.Label>
                  <DataList.Value>
                    <Link href="/documents/checklist.xlsx">
                      Ver informe <ExternalLinkIcon />
                    </Link>
                  </DataList.Value>
                </DataList.Item>
              </DataList.Root>

              <Flex gap="1" wrap="wrap">
                {vehicle.characteristics.map((c) => (
                  <Badge color="gray">{t(c)}</Badge>
                ))}
              </Flex>

              <Flex justify="end">
                <Dialog.Close>
                  <Button color="gray" onClick={() => setShowReport(false)}>
                    <Text>Cerrar</Text>
                  </Button>
                </Dialog.Close>
              </Flex>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      )}
    </>
  )
}
